// Enterprise Engagement Process
import React from 'react';
import { MessageSquare, FileSearch, FileText, Users, Rocket, CheckCircle2 } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';

interface ProcessStep {
  step: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  duration: string;
  deliverables: string[];
}

const processSteps: ProcessStep[] = [
  {
    step: 1,
    icon: <MessageSquare className="w-6 h-6" />,
    title: 'Discovery Call',
    description: 'A 30-minute conversation to understand your challenges, goals, and constraints.',
    duration: 'Free',
    deliverables: ['Initial assessment', 'High-level recommendations'],
  },
  {
    step: 2,
    icon: <FileSearch className="w-6 h-6" />,
    title: 'Technical Assessment',
    description: 'Deep dive into your existing systems, data, and infrastructure.',
    duration: '1-2 weeks',
    deliverables: ['Architecture review', 'Risk assessment', 'Integration roadmap'],
  },
  {
    step: 3,
    icon: <FileText className="w-6 h-6" />,
    title: 'Proposal & SOW',
    description: 'Detailed proposal with fixed scope, timeline, and transparent pricing.',
    duration: '3-5 days',
    deliverables: ['Technical specification', 'Fixed quote', 'Project timeline'],
  },
  {
    step: 4,
    icon: <Users className="w-6 h-6" />,
    title: 'Dedicated Team',
    description: 'Your assigned sprint team begins work with weekly progress updates.',
    duration: 'Project duration',
    deliverables: ['Weekly demos', 'Staging access', 'Slack channel'],
  },
  {
    step: 5,
    icon: <Rocket className="w-6 h-6" />,
    title: 'Launch & Optimize',
    description: 'Production deployment with monitoring, followed by 30-day optimization period.',
    duration: '30 days post-launch',
    deliverables: ['Production deployment', 'Performance tuning', 'Training'],
  },
];

export const EnterpriseProcess = () => {
  const { isVisible, elementRef } = useScrollReveal({ threshold: 0.1 });
  
  return (
    <section 
      ref={elementRef}
      className="relative py-24 lg:py-32 bg-[#0a0a0a]"
    >
      <div className="container-professional">
        {/* Header */}
        <div 
          className="text-center mb-16"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider mb-4">
            <CheckCircle2 className="w-4 h-4" />
            Engagement Process
          </span>
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4">
            How We{' '}
            <span className="text-primary">Work Together</span>
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            A transparent, structured approach from first conversation to production.
            No surprises. No hidden costs.
          </p>
        </div>
        
        {/* Process Steps */}
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Vertical line */}
            <div 
              className="absolute left-6 top-8 bottom-8 w-px bg-gradient-to-b from-primary via-primary/50 to-transparent hidden md:block"
              style={{
                opacity: isVisible ? 1 : 0,
                transition: 'opacity 0.8s ease 400ms',
              }}
            />
            
            {/* Steps */}
            <div className="space-y-6">
              {processSteps.map((step, index) => (
                <div 
                  key={step.step}
                  className="relative flex gap-6"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `all 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${index * 100}ms`,
                  }}
                >
                  {/* Step number */}
                  <div className="flex-shrink-0 relative z-10">
                    <div 
                      className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center text-primary"
                      style={{
                        boxShadow: '0 4px 16px -4px hsl(var(--primary) / 0.3)',
                      }}
                    >
                      {step.icon}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 pb-8">
                    <div className="p-6 rounded-xl border border-white/[0.06] bg-white/[0.01] hover:bg-white/[0.02] transition-colors">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-3">
                        <div>
                          <span className="text-xs font-mono text-primary mb-1 block">Step {step.step}</span>
                          <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                        </div>
                        <span className="text-xs font-medium text-white/40 bg-white/[0.04] px-3 py-1 rounded-full self-start">
                          {step.duration}
                        </span>
                      </div>
                      <p className="text-white/60 text-sm leading-relaxed mb-4">{step.description}</p>
                      
                      {/* Deliverables */}
                      <div className="flex flex-wrap gap-2">
                        {step.deliverables.map((deliverable) => (
                          <span 
                            key={deliverable}
                            className="inline-flex items-center gap-1.5 text-xs text-white/50"
                          >
                            <CheckCircle2 className="w-3 h-3 text-primary" />
                            {deliverable}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
